import {type RefObject, useEffect, useRef} from 'react';

import eventMessagesService from '@/services/events/eventMessagesService';

/**
 * Listens to scroll events on the given element ref and forwards
 * delta + scrollTop to the parent host app via postMessage (UWM_MODULE_SCROLL).
 * Allows the host to minimize/maximize its bottom navigation bar based
 * on scroll direction inside this iframe.
 *
 * Uses a callback ref pattern internally so the listener is re-attached
 * whenever the element changes (e.g. SlideTransition creates a new pane).
 */
const useScrollMessages = (scrollRef: RefObject<HTMLDivElement | null>) => {
    const lastScrollTopRef = useRef(0);

    useEffect(() => {
        const element = scrollRef.current;
        if (!element) return;

        lastScrollTopRef.current = element.scrollTop;

        const handleScroll = () => {
            const scrollTop = element.scrollTop;
            const delta = scrollTop - lastScrollTopRef.current;
            lastScrollTopRef.current = scrollTop;

            if (delta !== 0) {
                eventMessagesService.sendScrollMessage(delta, scrollTop);
            }
        };

        element.addEventListener('scroll', handleScroll, {passive: true});
        return () => element.removeEventListener('scroll', handleScroll);
    });
};

export default useScrollMessages;
